import {
  clearVideoAssetBinding,
  getVideoAssetFieldNames,
} from "../../../utils/guaVideoAssetStatus";

export const GENERATION_HISTORY_LIMIT = 20;

const EMPTY_DATE = "1970-01-01T00:00:00.000Z";
const SUCCESS_STATUSES = new Set(["completed", "complete", "success", "succeeded"]);
const ASSET_BINDINGS = Object.freeze([
  Object.freeze({
    provider: "guavideo",
    idField: "guaVideoAssetId",
    statusField: "guaVideoAssetStatus",
  }),
  Object.freeze({
    provider: "engguavideo",
    idField: "engGuaVideoAssetId",
    statusField: "engGuaVideoAssetStatus",
  }),
  Object.freeze({
    provider: "omini_seedance",
    idField: "guaOminiAssetId",
    statusField: "guaOminiAssetStatus",
  }),
  Object.freeze({
    provider: "dreamina_proxy",
    idField: "dreaminaProxyAssetId",
    statusField: "dreaminaProxyAssetStatus",
  }),
]);
const ASSET_FIELD_NAMES = ASSET_BINDINGS.flatMap(({ idField, statusField }) => [idField, statusField]);

const unwrapNodeData = (nodeOrData) => {
  if (!nodeOrData || typeof nodeOrData !== "object") return {};
  const nested = nodeOrData.data;
  if (nested && typeof nested === "object" && (nested.legacyType || nested.generateType)) {
    return nested;
  }
  return nodeOrData;
};

const hasOwn = (value, key) => Object.prototype.hasOwnProperty.call(value || {}, key);

const firstDefined = (...values) => values.find((value) => value !== undefined && value !== null);

const nullableString = (value) => {
  if (value === undefined || value === null) return null;
  return String(value);
};

const nullableId = (value) => (
  value === undefined || value === null || value === "" ? null : value
);

const normalizeMediaType = (value) => {
  const normalized = String(value || "").trim().toLowerCase();
  if (["image", "img", "picture"].includes(normalized)) return "image";
  if (["video", "movie"].includes(normalized)) return "video";
  return null;
};

const normalizeCreatedAt = (value, fallback = EMPTY_DATE) => {
  const candidate = firstDefined(value, fallback);
  const timestamp = new Date(candidate).getTime();
  return Number.isFinite(timestamp) ? new Date(timestamp).toISOString() : EMPTY_DATE;
};

const normalizeSuccessStatus = (value) => {
  if (value === undefined || value === null || value === "") return "completed";
  const normalized = String(value).trim().toLowerCase();
  return SUCCESS_STATUSES.has(normalized) ? "completed" : null;
};

// 稳定的小型散列仅用于浏览器端历史项 id，不承担安全用途。
const hashString = (value) => {
  let hash = 2166136261;
  const source = String(value || "");
  for (let index = 0; index < source.length; index += 1) {
    hash ^= source.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(36);
};

const historyIdentity = (entry) => (
  entry.taskId
    ? `task:${entry.taskId}`
    : `result:${entry.url}|${entry.createdAt}`
);

const makeHistoryId = ({ taskId, url, createdAt }) => (
  taskId
    ? `task:${taskId}`
    : `result:${hashString(`${url}|${createdAt}`)}`
);

const resolveAssetBinding = (source = {}) => {
  const explicitFields = getVideoAssetFieldNames(source.assetProvider);
  if (explicitFields) {
    const explicit = ASSET_BINDINGS.find(({ idField }) => idField === explicitFields.idField);
    if (explicit) return explicit;
  }

  const detected = ASSET_BINDINGS.filter(({ idField, statusField }) => (
    nullableId(source[idField]) !== null || nullableId(source[statusField]) !== null
  ));
  return detected.length === 1 ? detected[0] : null;
};

/**
 * 历史项始终保留各厂商素材库字段，但同一项只接受 assetProvider 对应的一套绑定。
 * 这让旧脏数据也不会在切换版本时把多个项目素材库同时带到当前节点。
 */
const sanitizeHistoryAssetBinding = (source = {}) => {
  const cleared = clearVideoAssetBinding({ assetProvider: null });
  const binding = resolveAssetBinding(source);
  if (!binding) return cleared;
  return {
    ...cleared,
    assetProvider: binding.provider,
    [binding.idField]: nullableId(source[binding.idField]),
    [binding.statusField]: nullableString(source[binding.statusField]),
  };
};

export const getGenerationMediaType = (nodeOrData) => {
  const data = unwrapNodeData(nodeOrData);
  if (data.legacyType !== "generate") return null;
  return normalizeMediaType(data.generateType || data.normalized?.kind || data.resourceType);
};

export const isGenerationHistoryNode = (nodeOrData) => (
  getGenerationMediaType(nodeOrData) !== null
);

// P2-1 之前的生成节点可能把当前产物写在 result/url 等字段；迁移历史时一并兜住，
// 否则第一次新生成清理旧字段后，旧产物会永久失去入口。
const getLegacyResultUrls = (data = {}) => [...new Set([
  data.url,
  data.lastResultUrl,
  data.result,
  data.generatedImage,
  data.generatedVideo,
].filter((value) => typeof value === "string" && value.trim()))];

// 旧渲染链路优先 data.url；与画布当前可见产物保持一致。
const getLegacyResultUrl = (data = {}) => getLegacyResultUrls(data)[0] || null;

/**
 * 构建一条成功的生成记录。生成参数可从节点回退；结果资源和素材库绑定默认不从
 * 当前节点继承，避免一次新生成误用上一个版本的 resourceId / AssetId。
 */
export const buildGenerationHistoryEntry = (nodeOrData, result = {}, options = {}) => {
  const data = unwrapNodeData(nodeOrData);
  const mediaType = getGenerationMediaType(data);
  if (!mediaType || !result || typeof result !== "object") return null;

  const suppliedMediaType = normalizeMediaType(
    firstDefined(result.mediaType, result.resourceType, result.generateType),
  );
  if (suppliedMediaType && suppliedMediaType !== mediaType) return null;

  const url = nullableString(firstDefined(result.url, result.resultUrl, result.lastResultUrl));
  const status = normalizeSuccessStatus(result.status);
  if (!url || !status) return null;

  const taskIdValue = firstDefined(result.taskId, result.task_id);
  const taskId = taskIdValue === undefined || taskIdValue === null || taskIdValue === ""
    ? null
    : String(taskIdValue);
  const createdAt = normalizeCreatedAt(
    firstDefined(result.createdAt, result.lastGeneratedAt),
    options.createdAtFallback || new Date().toISOString(),
  );
  const provider = nullableString(firstDefined(
    result.provider,
    result.imageProvider,
    result.videoProvider,
    mediaType === "image" ? data.imageProvider : null,
    data.videoProvider,
    data.provider,
  ));
  const model = nullableString(firstDefined(
    result.model,
    result.videoModel,
    data.videoModel,
    data.model,
  ));
  const resolution = nullableString(firstDefined(
    result.resolution,
    result.videoResolution,
    result.imageQualityTier,
    mediaType === "video" ? data.videoResolution : data.imageQualityTier,
    data.resolution,
  ));
  const duration = firstDefined(
    result.duration,
    result.videoDuration,
    mediaType === "video" ? firstDefined(data.videoDuration, data.duration) : null,
  );

  const bindingSource = options.inheritExistingBindings
    ? { ...data, ...result }
    : result;
  const assetBinding = sanitizeHistoryAssetBinding(bindingSource);
  const resourceId = options.inheritExistingBindings
    ? nullableId(hasOwn(result, "resourceId") ? result.resourceId : data.resourceId)
    : nullableId(result.resourceId);
  const originalVideoUrl = options.inheritExistingBindings
    ? nullableString(hasOwn(result, "originalVideoUrl") ? result.originalVideoUrl : data.originalVideoUrl)
    : nullableString(result.originalVideoUrl);

  return {
    id: nullableString(result.id) || makeHistoryId({ taskId, url, createdAt }),
    taskId,
    mediaType,
    url,
    createdAt,
    prompt: nullableString(firstDefined(
      result.prompt,
      result.imagePrompt,
      result.videoPrompt,
      data.prompt,
      data.imagePrompt,
      data.videoPrompt,
    )) || "",
    rawPrompt: nullableString(
      hasOwn(result, "rawPrompt")
        ? result.rawPrompt
        : firstDefined(
          result.prompt,
          result.imagePrompt,
          result.videoPrompt,
          data.rawPrompt,
          data.prompt,
          data.imagePrompt,
          data.videoPrompt,
        ),
    ) || "",
    lighting: hasOwn(result, "lighting")
      ? nullableString(result.lighting)
      : nullableString(options.lightingFallback !== undefined
        ? options.lightingFallback
        : data.lighting),
    provider,
    model,
    aspectRatio: nullableString(firstDefined(result.aspectRatio, data.aspectRatio)),
    resolution,
    duration: duration === undefined ? null : duration,
    resourceId,
    originalVideoUrl,
    ...assetBinding,
    status,

    // 恢复生成参数时使用；这些字段不参与版本切换。
    imageQualityTier: nullableString(firstDefined(
      result.imageQualityTier,
      mediaType === "image" ? resolution : null,
    )),
    imageProvider: nullableString(firstDefined(
      result.imageProvider,
      mediaType === "image" ? provider : null,
      data.imageProvider,
    )),
    includeProjectStyle: firstDefined(result.includeProjectStyle, data.includeProjectStyle),
    videoProvider: nullableString(firstDefined(result.videoProvider, provider)),
    videoModel: nullableString(firstDefined(result.videoModel, model)),
    videoDuration: firstDefined(result.videoDuration, mediaType === "video" ? duration : null),
    videoResolution: nullableString(firstDefined(
      result.videoResolution,
      mediaType === "video" ? resolution : null,
    )),
    guaVideoMode: nullableString(firstDefined(result.guaVideoMode, data.guaVideoMode)),
    seedance25Mode: nullableString(firstDefined(result.seedance25Mode, data.seedance25Mode)),
    guaAudio: firstDefined(result.guaAudio, data.guaAudio),
    guaWebSearch: firstDefined(result.guaWebSearch, data.guaWebSearch),
    wanxMode: nullableString(firstDefined(result.wanxMode, data.wanxMode)),
    wanxAudio: firstDefined(result.wanxAudio, data.wanxAudio),
    wanxWatermark: firstDefined(result.wanxWatermark, data.wanxWatermark),
    wanxPromptExtend: firstDefined(result.wanxPromptExtend, data.wanxPromptExtend),
    wanxSeed: firstDefined(result.wanxSeed, data.wanxSeed),
    wanxMedia: firstDefined(result.wanxMedia, data.wanxMedia),
    videoAudio: firstDefined(result.videoAudio, data.videoAudio),
    videoBgm: firstDefined(result.videoBgm, data.videoBgm),
  };
};

export const normalizeGenerationHistoryEntry = (nodeOrData, entry) => {
  if (!entry || typeof entry !== "object") return null;
  const data = unwrapNodeData(nodeOrData);
  return buildGenerationHistoryEntry(data, entry, {
    // 普通历史项缺字段时必须保持为空，不能从“当前版本”反向继承资源/AssetId。
    inheritExistingBindings: false,
    createdAtFallback: firstDefined(data.lastGeneratedAt, data.updatedAt, data.createdAt, EMPTY_DATE),
    lightingFallback: null,
  });
};

export const buildLegacyGenerationHistoryEntry = (nodeOrData, options = {}) => {
  const data = unwrapNodeData(nodeOrData);
  const legacyResultUrl = options.url || getLegacyResultUrl(data);
  const inheritExistingBindings = options.inheritExistingBindings !== false;
  if (!isGenerationHistoryNode(data) || !legacyResultUrl) return null;
  return buildGenerationHistoryEntry(data, {
    url: legacyResultUrl,
    createdAt: firstDefined(data.lastGeneratedAt, data.updatedAt, data.createdAt, EMPTY_DATE),
    prompt: firstDefined(data.prompt, data.imagePrompt, data.videoPrompt),
    rawPrompt: firstDefined(data.rawPrompt, data.prompt, data.imagePrompt, data.videoPrompt),
    lighting: data.lighting ?? null,
    provider: firstDefined(data.imageProvider, data.videoProvider, data.provider),
    model: firstDefined(data.videoModel, data.model),
    aspectRatio: data.aspectRatio,
    resolution: firstDefined(data.videoResolution, data.imageQualityTier, data.resolution),
    duration: firstDefined(data.videoDuration, data.duration),
    resourceId: inheritExistingBindings ? data.resourceId : null,
    originalVideoUrl: inheritExistingBindings ? data.originalVideoUrl : null,
    assetProvider: inheritExistingBindings ? data.assetProvider : null,
    ...Object.fromEntries(ASSET_FIELD_NAMES.map((field) => [
      field,
      inheritExistingBindings ? data[field] : null,
    ])),
    imageQualityTier: data.imageQualityTier,
    imageProvider: data.imageProvider,
    includeProjectStyle: data.includeProjectStyle,
    videoProvider: data.videoProvider,
    videoModel: data.videoModel,
    videoDuration: data.videoDuration,
    videoResolution: data.videoResolution,
    guaVideoMode: data.guaVideoMode,
    seedance25Mode: data.seedance25Mode,
    guaAudio: data.guaAudio,
    guaWebSearch: data.guaWebSearch,
    wanxMode: data.wanxMode,
    wanxAudio: data.wanxAudio,
    wanxWatermark: data.wanxWatermark,
    wanxPromptExtend: data.wanxPromptExtend,
    wanxSeed: data.wanxSeed,
    wanxMedia: data.wanxMedia,
    videoAudio: data.videoAudio,
    videoBgm: data.videoBgm,
    status: "completed",
  }, {
    inheritExistingBindings,
    createdAtFallback: EMPTY_DATE,
  });
};

/**
 * 返回最新在前的、已去重的有效历史。旧节点只有旧产物字段时，会合成首条记录。
 */
export const normalizeGenerationHistory = (nodeOrData, options = {}) => {
  const data = unwrapNodeData(nodeOrData);
  if (!isGenerationHistoryNode(data)) return [];

  const limit = Math.min(
    GENERATION_HISTORY_LIMIT,
    Math.max(1, Number(options.limit) || GENERATION_HISTORY_LIMIT),
  );
  const source = Array.isArray(data.generationHistory) ? data.generationHistory : [];
  const normalized = [];
  const seen = new Set();

  source.forEach((candidate) => {
    const entry = normalizeGenerationHistoryEntry(data, candidate);
    if (!entry) return;
    const identity = historyIdentity(entry);
    if (seen.has(identity)) return;
    seen.add(identity);
    normalized.push(entry);
  });

  if (options.includeLegacy !== false) {
    const legacyEntries = [];
    getLegacyResultUrls(data).forEach((url, index) => {
      if (normalized.some((entry) => entry.url === url)) return;
      const legacyEntry = buildLegacyGenerationHistoryEntry(data, {
        url,
        // 顶层绑定属于旧界面当前实际展示的第一条 URL；其余旧字段只保留版本入口。
        inheritExistingBindings: index === 0,
      });
      if (legacyEntry) legacyEntries.push(legacyEntry);
    });
    normalized.unshift(...legacyEntries);
  }

  return normalized.slice(0, limit);
};

const appendBuiltEntry = (nodeOrData, entry) => {
  const data = unwrapNodeData(nodeOrData);
  if (!entry) return { history: normalizeGenerationHistory(data), entry: null };

  const identity = historyIdentity(entry);
  const current = normalizeGenerationHistory(data);
  const previous = current.find((candidate) => historyIdentity(candidate) === identity);
  const incomingAssetBinding = resolveAssetBinding(entry);
  const preservedAssetBinding = previous && !incomingAssetBinding
    ? {
      assetProvider: previous.assetProvider,
      ...Object.fromEntries(ASSET_FIELD_NAMES.map((field) => [field, previous[field]])),
    }
    : {};
  const merged = previous
    ? normalizeGenerationHistoryEntry(data, {
      ...previous,
      ...entry,
      // 同一任务完成回调重复到达时，不能抹掉先前已异步回填的持久化元数据。
      resourceId: entry.resourceId ?? previous.resourceId,
      originalVideoUrl: entry.originalVideoUrl ?? previous.originalVideoUrl,
      ...preservedAssetBinding,
      id: previous.id,
    })
    : entry;
  const history = [
    merged,
    ...current.filter((candidate) => historyIdentity(candidate) !== identity),
  ].slice(0, GENERATION_HISTORY_LIMIT);
  return { history, entry: merged };
};

// AtomEditPanel 的 formData 会展开整个节点 data，其中包含上一版本的资源/素材绑定。
// 新生成结果必须从空绑定开始；resourceId 与 AssetId 只能随后按历史 id 异步回填。
const buildFreshGenerationHistoryEntry = (nodeOrData, result = {}) => (
  buildGenerationHistoryEntry(nodeOrData, {
    ...result,
    resourceId: null,
    assetProvider: null,
    ...Object.fromEntries(ASSET_FIELD_NAMES.map((field) => [field, null])),
  })
);

/** 幂等追加一条成功结果；同一 taskId，或相同 URL + createdAt，只保留一条。 */
export const appendGenerationHistory = (nodeOrData, result) => {
  const entry = buildFreshGenerationHistoryEntry(nodeOrData, result);
  return appendBuiltEntry(nodeOrData, entry).history;
};

const findHistoryEntry = (nodeOrData, entryOrId) => {
  const data = unwrapNodeData(nodeOrData);
  const id = typeof entryOrId === "object" ? entryOrId?.id : entryOrId;
  if (id === undefined || id === null) return null;
  return normalizeGenerationHistory(data).find((entry) => String(entry.id) === String(id)) || null;
};

export const getCurrentGenerationHistoryEntry = (nodeOrData) => {
  const data = unwrapNodeData(nodeOrData);
  const history = normalizeGenerationHistory(data);
  if (data.currentGenerationHistoryId !== undefined && data.currentGenerationHistoryId !== null) {
    const byId = history.find((entry) => String(entry.id) === String(data.currentGenerationHistoryId));
    if (byId) return byId;
  }
  return history.find((entry) => entry.url === getLegacyResultUrl(data)) || history[0] || null;
};

/**
 * 选择版本只切换展示结果及该版本自己的资源绑定。结果写入 lastResultUrl，并清掉
 * 可能压过它的旧产物字段；节点仍保持 generate 身份，不会改写成 resource 节点。
 */
export const selectGenerationHistoryVersion = (nodeOrData, entryOrId) => {
  const data = unwrapNodeData(nodeOrData);
  const entry = findHistoryEntry(data, entryOrId);
  if (!entry) return {};

  const cleared = clearVideoAssetBinding({
    currentGenerationHistoryId: entry.id,
    lastResultUrl: entry.url,
    lastGeneratedAt: entry.createdAt,
    url: null,
    thumbnail: null,
    result: null,
    generatedImage: null,
    generatedVideo: null,
    resourceId: nullableId(entry.resourceId),
    originalVideoUrl: nullableString(entry.originalVideoUrl),
    assetProvider: null,
  });
  const binding = resolveAssetBinding(entry);
  const ownBinding = binding
    ? {
      assetProvider: binding.provider,
      [binding.idField]: nullableId(entry[binding.idField]),
      [binding.statusField]: nullableString(entry[binding.statusField]),
    }
    : {};

  return {
    ...cleared,
    ...ownBinding,
    isGenerating: false,
    generatingStatus: "completed",
    errorMessage: null,
    activeTaskId: null,
    activeTaskKind: null,
    activeTaskProvider: null,
    activeTaskModel: null,
    activeTaskProjectId: null,
    generationStartedAt: null,
  };
};

/** 成功完成任务时一次生成可直接交给 onUpdate 的安全 patch。 */
export const createSuccessfulGenerationPatch = (nodeOrData, result) => {
  const data = unwrapNodeData(nodeOrData);
  const built = buildFreshGenerationHistoryEntry(data, result);
  const appended = appendBuiltEntry(data, built);
  if (!appended.entry) return {};

  const virtualData = {
    ...data,
    generationHistory: appended.history,
    currentGenerationHistoryId: appended.entry.id,
    lastResultUrl: appended.entry.url,
  };
  return {
    ...selectGenerationHistoryVersion(virtualData, appended.entry.id),
    generationHistory: appended.history,
  };
};

const pickBackfillFields = (updates = {}) => {
  const picked = {};
  ["resourceId", "originalVideoUrl", "assetProvider", ...ASSET_FIELD_NAMES].forEach((field) => {
    if (hasOwn(updates, field)) picked[field] = updates[field];
  });
  return picked;
};

/**
 * 按历史 id 回填异步资源中心/素材入库结果。旧任务回调只更新自己的历史项；仅当该
 * 项仍是当前版本时才同步节点顶层绑定，避免旧回调覆盖用户刚切换的新版本。
 */
export const backfillGenerationHistoryEntry = (nodeOrData, entryId, updates = {}) => {
  const data = unwrapNodeData(nodeOrData);
  const history = normalizeGenerationHistory(data);
  const index = history.findIndex((entry) => String(entry.id) === String(entryId));
  if (index < 0) return {};

  const previous = history[index];
  const picked = pickBackfillFields(updates);
  const hasAssetUpdate = hasOwn(picked, "assetProvider")
    || ASSET_FIELD_NAMES.some((field) => hasOwn(picked, field));
  const mergedSource = hasAssetUpdate
    ? { ...previous, ...picked, ...sanitizeHistoryAssetBinding({ ...previous, ...picked }) }
    : { ...previous, ...picked };
  const nextEntry = normalizeGenerationHistoryEntry(data, mergedSource);
  if (!nextEntry) return {};

  const nextHistory = [...history];
  nextHistory[index] = nextEntry;
  const patch = { generationHistory: nextHistory };
  const currentId = data.currentGenerationHistoryId;
  const isCurrent = currentId !== undefined && currentId !== null
    ? String(currentId) === String(previous.id)
    : getLegacyResultUrl(data) === previous.url;
  if (!isCurrent) return patch;

  if (hasOwn(picked, "resourceId")) patch.resourceId = nullableId(nextEntry.resourceId);
  if (hasOwn(picked, "originalVideoUrl")) {
    patch.originalVideoUrl = nullableString(nextEntry.originalVideoUrl);
  }
  if (hasAssetUpdate) {
    const currentBindingPatch = selectGenerationHistoryVersion(
      { ...data, generationHistory: nextHistory, currentGenerationHistoryId: nextEntry.id },
      nextEntry.id,
    );
    patch.assetProvider = currentBindingPatch.assetProvider;
    ASSET_FIELD_NAMES.forEach((field) => {
      patch[field] = currentBindingPatch[field];
    });
  }
  return patch;
};

/** 恢复某一版本当时的提示词/生成参数，不切换当前展示结果。 */
export const restoreGenerationHistoryParameters = (nodeOrData, entryOrId) => {
  const data = unwrapNodeData(nodeOrData);
  const entry = findHistoryEntry(data, entryOrId);
  if (!entry) return {};

  const restoredPrompt = entry.rawPrompt !== undefined && entry.rawPrompt !== null
    ? entry.rawPrompt
    : (entry.prompt || "");
  const patch = {
    prompt: restoredPrompt,
    rawPrompt: restoredPrompt,
    lighting: entry.lighting ?? null,
  };
  if (entry.aspectRatio) patch.aspectRatio = entry.aspectRatio;

  if (entry.mediaType === "image") {
    if (entry.imageProvider || entry.provider) {
      patch.imageProvider = entry.imageProvider || entry.provider;
    }
    const imageQualityTier = entry.imageQualityTier || entry.resolution;
    if (imageQualityTier) patch.imageQualityTier = imageQualityTier;
    if (entry.includeProjectStyle !== undefined) {
      patch.includeProjectStyle = entry.includeProjectStyle;
    }
    return patch;
  }

  if (entry.videoProvider || entry.provider) patch.videoProvider = entry.videoProvider || entry.provider;
  if (entry.videoModel || entry.model) patch.videoModel = entry.videoModel || entry.model;
  if (entry.videoDuration !== undefined && entry.videoDuration !== null) {
    patch.videoDuration = entry.videoDuration;
  }
  if (entry.videoResolution || entry.resolution) {
    patch.videoResolution = entry.videoResolution || entry.resolution;
  }
  if (entry.guaVideoMode) patch.guaVideoMode = entry.guaVideoMode;
  if (entry.seedance25Mode) patch.seedance25Mode = entry.seedance25Mode;
  if (entry.guaAudio !== undefined) patch.guaAudio = entry.guaAudio;
  if (entry.guaWebSearch !== undefined) patch.guaWebSearch = entry.guaWebSearch;
  if (entry.wanxMode) patch.wanxMode = entry.wanxMode;
  if (entry.wanxAudio !== undefined) patch.wanxAudio = entry.wanxAudio;
  if (entry.wanxWatermark !== undefined) patch.wanxWatermark = entry.wanxWatermark;
  if (entry.wanxPromptExtend !== undefined) patch.wanxPromptExtend = entry.wanxPromptExtend;
  if (entry.wanxSeed !== undefined) patch.wanxSeed = entry.wanxSeed;
  if (entry.wanxMedia !== undefined) patch.wanxMedia = entry.wanxMedia;
  if (entry.videoAudio !== undefined) patch.videoAudio = entry.videoAudio;
  if (entry.videoBgm !== undefined) patch.videoBgm = entry.videoBgm;
  return patch;
};
